import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, UserPlus, Upload, Shield, Layers, ArrowUpRight, ChevronLeft, ChevronRight, Users } from "lucide-react";
import Header from ".././components/Header";
import CreateUserModal from "./components/CreateUserModal";
import BulkUploadModal from "./components/BulkUploadModal";
import api from "../connector/api";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [groups, setGroups] = useState([]); 
  const [selectedStatus, setSelectedStatus] = useState(""); // "", "active", or "inactive"
  const [isGroupDropdownOpen, setIsGroupDropdownOpen] = useState(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  
  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);

  const [isCreateOpen, setCreateOpen] = useState(false);
  const [isBulkOpen, setBulkOpen] = useState(false);

  const navigate = useNavigate();

  // 1. Fetch Structural System Groups for the dropdown filter menu
  useEffect(() => {
    api.get("groups/") 
      .then(res => setGroups(Array.isArray(res.data) ? res.data : res.data.results || []))
      .catch(err => console.error("Failed to load layout groups", err));
  }, []);

  // 2. Main paginated profile user retrieval loop
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        let url = `users/?page=${currentPage}&search=${search}&is_active=${
          selectedStatus === "active" ? "true" : selectedStatus === "inactive" ? "false" : ""
        }`;

        if (selectedGroup) {
          url += `&group=${selectedGroup}`;
        }

        const res = await api.get(url);
        
        const rawResults = res.data.results || [];
        setTotalCount(res.data.count || 0);
        setHasNext(!!res.data.next);
        setHasPrev(!!res.data.previous);

        const normalized = rawResults.map((u) => ({
          id: u.id,
          username: u.username,
          email: u.email,
          first_name: u.first_name || u.employee?.first_name || "",
          last_name: u.last_name || u.employee?.last_name || "",
          status: u.employee?.status || "Inactive",
          is_active: u.is_active || false,
          permissions: u.permissions || [],
        }));
        
        setUsers(normalized);
      } catch (err) {
        console.error("Failed to fetch users", err);
      }
    };
    
    fetchUsers();
  }, [currentPage, search, selectedGroup, selectedStatus]);

  // Unified status filter handler update
  const handleSelectStatus = (statusValue) => {
    setSelectedStatus(statusValue);
    setCurrentPage(1); 
    setIsStatusDropdownOpen(false);
  };

  // Unified group filter handler update
  const handleSelectGroup = (groupId) => {
    setSelectedGroup(groupId);
    setCurrentPage(1); 
    setIsGroupDropdownOpen(false);
  };

  return (
    <div className="flex flex-col h-screen overflow-hidden font-dynamic-title bg-slate-50">
      <Header />

      {/* Main Container Wrapper: Configured to fix position layouts cleanly */}
      <div className="flex-1 min-h-0 flex flex-col p-6 sm:p-8 max-w-7xl w-full mx-auto space-y-6">
        
        {/* Top Operational Action Bar (Stays Fixed) */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200 pb-5 flex-shrink-0">
          <div>
            <h1 className="text-lg font-bold tracking-tight text-slate-900">User Identity Registry</h1>
            <p className="text-xs text-slate-400 font-medium mt-1">Manage corporate personnel accounts, access tokens, and permission matrices.</p>
          </div>
          
          <div className="flex items-center gap-2.5">
            <button
              onClick={() => setBulkOpen(true)}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 border border-slate-200 bg-white hover:bg-slate-50 transition-all text-xs font-semibold text-slate-700 rounded-xl shadow-xs"
            >
              <Upload className="w-3.5 h-3.5 text-slate-400" />
              Bulk Import
            </button>
            <button
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#002855] bg-opacity-80 hover:bg-opacity-95 transition-all text-xs font-semibold text-white rounded-xl shadow-xs"
            >
              <UserPlus className="w-3.5 h-3.5" />
              Add Individual User
            </button>
          </div>
        </div>

        {/* Dynamic Multi-Filter Action Row (Stays Fixed) */}
        <div className="flex flex-col md:flex-row items-center gap-3 w-full flex-shrink-0">
          {/* Search Input element */}
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search matching identities by name, email, or credentials..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              className="w-full bg-white border border-slate-200 rounded-xl py-2 pl-10 pr-4 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400 shadow-2xs transition-all"
            />
          </div>

          {/* 21st.dev Premium Custom Dropdown: Group Selector */}
          <div className="relative w-full md:w-56">
            <button
              type="button"
              onClick={() => {
                setIsGroupDropdownOpen(!isGroupDropdownOpen);
                setIsStatusDropdownOpen(false);
              }}
              className="flex items-center justify-between w-full bg-white border border-slate-200 rounded-xl py-2 px-3.5 text-xs font-medium text-slate-700 hover:bg-slate-50 shadow-2xs transition-all text-left"
            >
              <span className="truncate">
                {groups.find(g => String(g.id) === String(selectedGroup))?.name || "All Structural Groups"}
              </span>
              <Users className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
            </button>

            {isGroupDropdownOpen && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setIsGroupDropdownOpen(false)} />
                <div className="absolute right-0 mt-1.5 w-full bg-white border border-slate-200/80 rounded-xl shadow-lg py-1 z-30 animate-in fade-in slide-in-from-top-1 duration-100 max-h-60 overflow-y-auto scrollbar-thin">
                  <button
                    type="button"
                    onClick={() => handleSelectGroup("")}
                    className={`w-full text-left px-3.5 py-2 text-xs font-medium hover:bg-slate-50 transition-colors ${selectedGroup === "" ? "text-slate-900 bg-slate-50/50 font-semibold" : "text-slate-600"}`}
                  >
                    All Structural Groups
                  </button>
                  {groups.map((g) => (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => handleSelectGroup(g.id)}
                      className={`w-full text-left px-3.5 py-2 text-xs font-medium hover:bg-slate-50 transition-colors truncate ${String(selectedGroup) === String(g.id) ? "text-slate-900 bg-slate-50/50 font-semibold" : "text-slate-600"}`}
                    >
                      {g.name}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* 21st.dev Premium Custom Dropdown: Account Status Filter */}
          <div className="relative w-full md:w-48">
            <button
              type="button"
              onClick={() => {
                setIsStatusDropdownOpen(!isStatusDropdownOpen);
                setIsGroupDropdownOpen(false);
              }}
              className="flex items-center justify-between w-full bg-white border border-slate-200 rounded-xl py-2 px-3.5 text-xs font-medium text-slate-700 hover:bg-slate-50 shadow-2xs transition-all text-left"
            >
              <span className="flex items-center gap-2">
                <span className={`w-1.5 h-1.5 rounded-full ${
                  selectedStatus === "active" ? "bg-emerald-500" :
                  selectedStatus === "inactive" ? "bg-rose-500" : "bg-slate-400"
                }`} />
                {selectedStatus === "active" ? "Active Accounts" :
                selectedStatus === "inactive" ? "Suspended Accounts" : "All Account Statuses"}
              </span>
              <Shield className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
            </button>

            {isStatusDropdownOpen && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setIsStatusDropdownOpen(false)} />
                <div className="absolute right-0 mt-1.5 w-full bg-white border border-slate-200/80 rounded-xl shadow-lg py-1 z-30 animate-in fade-in slide-in-from-top-1 duration-100">
                  <button
                    type="button"
                    onClick={() => handleSelectStatus("")}
                    className={`flex items-center gap-2 w-full text-left px-3.5 py-2 text-xs font-medium hover:bg-slate-50 transition-colors ${selectedStatus === "" ? "text-slate-900 bg-slate-50/50 font-semibold" : "text-slate-600"}`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                    All Account Statuses
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSelectStatus("active")}
                    className={`flex items-center gap-2 w-full text-left px-3.5 py-2 text-xs font-medium hover:bg-slate-50 transition-colors ${selectedStatus === "active" ? "text-slate-900 bg-slate-50/50 font-semibold" : "text-slate-600"}`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    Active Only
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSelectStatus("inactive")}
                    className={`flex items-center gap-2 w-full text-left px-3.5 py-2 text-xs font-medium hover:bg-slate-50 transition-colors ${selectedStatus === "inactive" ? "text-slate-900 bg-slate-50/50 font-semibold" : "text-slate-600"}`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                    Suspended Only
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Core Content Grid Table Area (Dynamic Scroll Viewports Layer) */}
        {users.length > 0 ? (
          <div className="flex-1 min-h-0 flex flex-col space-y-4">
            {/* Table Frame Container */}
            <div className="flex-1 min-h-0 bg-white border border-slate-200 rounded-xl shadow-2xs flex flex-col overflow-hidden">
              {/* This inner div handles the actual internal vertical table scroll independently */}
              <div className="flex-1 overflow-y-auto scrollbar-thin">
                <table className="w-full text-left border-collapse">
                  <thead className="sticky top-0 bg-slate-50 z-10 shadow-[0_1px_0_0_rgba(226,232,240,1)]">
                    <tr className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                      <th className="p-4 pl-6">Profile Identity</th>
                      <th className="p-4">Username</th>
                      <th className="p-4">Access Directives</th>
                      <th className="p-4">State</th>
                      <th className="p-4 pr-6 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-xs font-medium text-slate-700">
                    {users.map((user) => (
                      <tr 
                        key={user.id} 
                        onClick={() => navigate(`/users/${user.id}`)}
                        className="hover:bg-slate-50/70 transition-colors cursor-pointer group"
                      >
                        <td className="p-4 pl-6">
                          <div className="flex flex-col">
                            <span className="font-semibold text-slate-800 group-hover:text-slate-900 transition-colors">
                              {user.last_name || user.first_name ? `${user.last_name} ${user.first_name}` : "—"}
                            </span>
                            <span className="text-[11px] text-slate-400 font-mono mt-0.5">{user.email}</span>
                          </div>
                        </td>
                        <td className="p-4 font-mono text-slate-600">{user.username}</td>
                        <td className="p-4">
                          <div className="flex items-center gap-1.5 text-slate-500">
                            <Shield className="w-3.5 h-3.5 text-slate-400" />
                            <span className="font-mono text-[11px]">
                              {user.permissions?.length || 0} Rule keys
                            </span>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wide border ${
                            user.is_active
                              ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                              : "bg-rose-50 text-rose-700 border-rose-100"
                          }`}>
                            {user.is_active ? "Active" : "Suspended"}
                          </span>
                        </td>
                        <td className="p-4 pr-6 text-right">
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 group-hover:text-slate-800 transition-colors">
                            Inspect Ledger
                            <ArrowUpRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Premium Pagination Control Footer (Stays Anchored) */}
            <div className="flex items-center justify-between border border-slate-200 bg-white rounded-2xl px-5 py-3 shadow-2xs flex-shrink-0">
              <span className="text-xs font-medium text-slate-400 font-mono">
                Showing entries <span className="text-slate-700 font-bold">{(currentPage - 1) * 10 + 1}-{Math.min(currentPage * 10, totalCount)}</span> out of <span className="text-slate-700 font-bold">{totalCount}</span>
              </span>
              
              <div className="flex items-center gap-2">
                <button
                  disabled={!hasPrev}
                  onClick={() => setCurrentPage(prev => prev - 1)}
                  className="inline-flex items-center gap-1 p-2 border border-slate-200 text-xs font-semibold text-slate-600 rounded-xl bg-white hover:bg-slate-50 transition-all disabled:opacity-40 disabled:hover:bg-white cursor-pointer disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs font-bold text-slate-700 px-2 font-mono">
                  {currentPage}
                </span>
                <button
                  disabled={!hasNext}
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  className="inline-flex items-center gap-1 p-2 border border-slate-200 text-xs font-semibold text-slate-600 rounded-xl bg-white hover:bg-slate-50 transition-all disabled:opacity-40 disabled:hover:bg-white cursor-pointer disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* Empty Search/Filter Fallback Template */
          <div className="flex flex-col items-center justify-center py-16 text-center bg-white border border-dashed border-slate-200 rounded-3xl p-6 flex-shrink-0">
            <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100 mb-4">
              <Layers className="w-5 h-5 text-slate-400" />
            </div>
            <h3 className="text-sm font-bold text-slate-800">No identity indices matching parameters</h3>
            <p className="text-xs text-slate-400 max-w-xs mx-auto mt-1">
              Verify your spelling alignments or adjust structural filter configurations.
            </p>
            <button
              onClick={() => setCreateOpen(true)}
              className="mt-4 inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-[#002855] bg-opacity-80 hover:bg-opacity-95 text-white text-xs font-semibold rounded-xl transition-all"
            >
              <UserPlus className="w-3.5 h-3.5" />
              Register Profile
            </button>
          </div>
        )}
      </div>

      {/* Shared Modals Layer */}
      <CreateUserModal
        isOpen={isCreateOpen}
        onClose={() => setCreateOpen(false)}
        onUserCreated={(newUser) => setUsers((prev) => [...prev, newUser])}
      />
      <BulkUploadModal
        isOpen={isBulkOpen}
        onClose={() => setBulkOpen(false)}
        onUsersUploaded={(newUsers) => setUsers((prev) => [...prev, ...newUsers])}
      />
    </div>
  );
};

export default UserManagement;